'use client';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { usePathname } from 'next/navigation';

import { useHeaderVisibilityStore } from '@providers/header-visibility-store-provider';
import { useNavigate } from 'hooks/useNavigation';
import { RoleType } from 'types/roles';

import { Header, PageContent, PageGrid } from '@components/Common';

import { extractRoleFromPath } from '@utils/helpers/extractRole';

import { ArticleTitle, TermsContainer, TermsText } from './styles';

/**
 * Terms and Conditions component
 */

const ARTICLES = [
  { key: 'article1', items: [1, 2, 3, 4, 5, 6] },
  { key: 'article2', items: [1, 2, 3] },
  { key: 'article3', items: [1, 2, 3] },
  { key: 'article4', items: [1, 2, 3, 4, 5] },
  { key: 'article5', items: [1, 2, 3] },
  { key: 'article6', items: [1, 2] },
  { key: 'article7', items: [1, 2, 3] },
  { key: 'article8', items: [1, 2] },
  { key: 'article9', items: [1, 2] },
  { key: 'article10', items: [1, 2] },
];

export const TermsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { goToPath } = useNavigate();
  const pathname = usePathname();
  const setShowBackButton = useHeaderVisibilityStore((state) => state.setShowBackButton);
  const setOnBack = useHeaderVisibilityStore((state) => state.setOnBack);

  // Extract role from current path
  const role = extractRoleFromPath(pathname || '') as RoleType;

  // Handle back navigation to registration page
  const handleBack = useCallback(() => {
    if (role) {
      goToPath(`/${role}/register`);
    }
  }, [role, goToPath]);

  // Set up header back button
  useEffect(() => {
    setShowBackButton(true);
    setOnBack(handleBack);

    // Clean up on unmount
    return () => {
      setShowBackButton(false);
      setOnBack(undefined);
    };
  }, [setShowBackButton, setOnBack, handleBack]);

  return (
    <PageGrid className="full-height">
      <PageContent>
        <Header>{t('terms.title')}</Header>
        <TermsContainer>
          <TermsText>{t('terms.version')}</TermsText>
          <TermsText>{t('terms.date')}</TermsText>
        </TermsContainer>
        {ARTICLES.map(({ key, items }) => (
          <div key={key}>
            <ArticleTitle>{t(`terms.${key}.title`)}</ArticleTitle>
            {items.map((itemNum) => (
              <TermsText key={`${key}-item${itemNum}`}>
                {t(`terms.${key}.item${itemNum}`)}
              </TermsText>
            ))}
          </div>
        ))}
      </PageContent>
    </PageGrid>
  );
};
